import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Link from '~/components/Link/Link';
import { Container, Typo, Form as UIKitForm, Input as UIKitInput } from 'tmt-ui-kit';
import { connect } from 'react-redux';
import { actions as infoActions } from '~/redux/reducers/info';

import NotFound from '~/components/NotFound/NotFound';
import NoSSR from '~/components/NoSSR';

import config from '~/config';

const InnerContainer = styled(Container.InnerContainer)`
  padding-top: 16px;
  margin-bottom: 32px;
  p {
    font-family: 'Open Sans', sans-serif;
    a {
      font-family: 'Open Sans', sans-serif;
    }
  }
`;

const Title = Typo.HeaderOne;

const Topic = styled.p`
  font-size: 18px;
  font-weight: 400;
  ${props => props.error && `color: ${props.theme.red};`}
`;

const Block = styled.div`
  display: flex;
  justify-content: space-between;
  @media (max-width: 600px) {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const Content = styled.div`
  width: 50%;
  margin-bottom: 24px;
  @media (max-width: 768px) {
    width: 45%;
  }
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const Picture = styled.picture`
  border-radius: 6px;
`;

const TextBlock = styled.div`
  padding: 0 16px;
`;

const FormContainer = styled(Content)`
  ${props => !props.noPad && 'padding: 0 16px;'}
  display: flex;
  justify-content: flex-end;
  @media (max-width: 768px) {
    width: 55%;
  }
  @media (max-width: 600px) {
    justify-content: center;
    width: 100%;
  }
`;

const Form = styled.form`
  display: block;
  padding: 25px 28px 35px 28px;
  background-color: ${props => props.theme.blueGrayLighter};
  border-radius: 7px;
  height: fit-content;
  @media (max-width: 600px) {
    padding: 10px 15px 20px 15px;
  }
`;

const Input = styled(UIKitForm.Input)`
  margin-bottom: 8px;
  @media (min-width: 601px) {
    width: 320px;
  }
`;

const CheckBox = styled(UIKitForm.CheckBox)`
  margin-top: 24px;
  max-width: 320px;
`;

const Indent = styled.div`
  margin-top: 16px;
  padding-left: 16px;
`;

const ButtonContainer = styled.div`
  width: 320px;
  margin-top: 32px;
  @media (max-width: 600px) {
    width: 100%;
    padding: 0 8px;
  }
  > p {
    text-align: center;
    color: ${props => props.theme.red};
  }
`;

const SentBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  text-align: center;
`;

const ThanksWord = styled.p`
  color: ${props => props.theme.orange};
  font-weight: 600;
  font-size: 24px;
`;

const getLabelChoices = (questions) => questions.map(question => question.choices.map(c => ({
  label: c,
  value: c,
  active: questions.some(q => q.answer === c)
})));

const updateQuestion = (questions, question, answer) => questions.map(q => ((q.question === question) ? { ...q, answer } : q));

const validateForm = (form) => {
  const { name, email, questions, agreeTerm } = form;
  const formError = {};
  if (name.length <= 0) {
    formError.name = true;
  }
  if (email.length <= 0 || !email.match(/^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)) { // eslint-disable-line
    formError.email = true;
  }
  if (questions.some(q => q.answer.length <= 0)) {
    formError.questions = true;
  }
  if (!agreeTerm) {
    formError.agreeTerm = true;
  }
  return formError;
};

const enhance = connect(
  state => ({
    ebooks: state.info.ebooks,
    submittingEbookForm: state.info.submittingEbookForm,
    ebookFormSubmitted: state.info.ebookFormSubmitted
  }),
  {
    submitEbookForm: infoActions.submitEbookForm
  }
);

const EBook = (props) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    questions: [
      {
        question: 'When will you plan to visit Thailand?',
        choices: ['Currently in Thailand', 'Within 7 days', 'Within 1 month', 'Within 3 months', 'No plan yet'],
        answer: ''
      }
    ],
    agreeTerm: false,
    error: {}
  });

  const setFormField = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const submitForm = (e, ebook) => {
    e.preventDefault();
    const formError = validateForm(form);
    setFormField('error', formError);
    if (Object.keys(formError).length === 0) {
      props.submitEbookForm({
        name: form.name,
        email: form.email,
        questions: form.questions.map(q => ({ question: q.question, answer: q.answer })),
        ebookName: ebook.name,
        slug: ebook.slug
      });
    }
  };

  const { submittingEbookForm, ebookFormSubmitted } = props;
  const { name, email, questions, agreeTerm, error } = form;
  const isError = Object.keys(error).length > 0;
  const labelChoices = getLabelChoices(questions);
  const ebook = props.ebooks.find(e => e.slug === props.url.query.slug);
  return !ebook ? <NotFound /> : (
    <InnerContainer>
      <Title>{ebook.name}</Title>
      <Block>
        <Content>
          <Picture>
            <source srcSet={`${ebook.image_url_without_type}.webp`} type="image/webp" />
            <img src={`${ebook.image_url_without_type}.png`} alt={ebook.name} />
          </Picture>
          <TextBlock>{ebook.description}</TextBlock>
        </Content>
        <NoSSR>
          <FormContainer noPad={ebookFormSubmitted}>
            {!ebookFormSubmitted
              ? (
                <Form onSubmit={e => submitForm(e, ebook)}>
                  <Topic error={error.name}>Name</Topic>
                  <Input type="text" value={name} onChange={e => setFormField('name', e.target.value)} error={error.name} required />
                  <Topic error={error.email}>Email</Topic>
                  <Input type="email" value={email} onChange={e => setFormField('email', e.target.value)} error={error.email} required />
                  <Topic error={error.questions}>When will you plan to visit Thailand?</Topic>
                  {questions && questions.length > 0 && questions.map((q, i) => (
                    <Indent key={q.question}>
                      <UIKitForm.RadioButton error onChange={value => setFormField('questions', updateQuestion(questions, q.question, value))} values={labelChoices[i]} />
                    </Indent>
                  ))}
                  <CheckBox
                    onChange={value => setFormField('agreeTerm', value)}
                    checked={agreeTerm}
                    error={error.agreeTerm}
                    renderLabel={() => <span>Check this box to agree to receive emails with the updates and other news from TakeMeTour <Link to="/article/terms-conditions-and-disclaimer" target="_blank" rel="noopener noreferrer">(T&amp;Cs apply)</Link></span>}
                  />
                  <ButtonContainer>
                    <UIKitInput.SubmitButton type="submit" text={submittingEbookForm ? 'Submitting' : 'Submit to Download'}/>
                    {isError && <p>Please complete the form to download.</p>}
                  </ButtonContainer>
                </Form>
              )
              : (
                <SentBlock>
                  <img
                    src={`${config.assetPath}/static/images/icons/email.png`}
                    srtSet={`${config.assetPath}/static/images/icons/email@2x.png 2x`}
                    alt="email"
                  />
                  <ThanksWord>Thanks for your interest!</ThanksWord>
                  <p>The eBook will be sent to your email.</p>
                </SentBlock>
              )
            }
          </FormContainer>
        </NoSSR>
      </Block>
    </InnerContainer>
  );
};

EBook.getInitialProps = async ({ store, query, res }) => {
  const { slug } = query;
  if (!slug) {
    res.statusCode = 404;
    return {};
  }
  await store.dispatch(infoActions.getEbooks());
  return {};
};

EBook.propTypes = {
  url: PropTypes.object,
  ebooks: PropTypes.arrayOf(PropTypes.object),
  submitEbookForm: PropTypes.func,
  submittingEbookForm: PropTypes.bool,
  ebookFormSubmitted: PropTypes.bool
};

export default enhance(EBook);
